import { useState, useEffect } from "react";
import { getDeals, getDealsAdmin, createDeal, updateDeal, deleteDeal } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { 
  Gift, Plus, Trash2, Edit, X, Loader2, Check, Calendar, 
  Users, Globe, Building, Tag, ChevronDown, ChevronUp, Copy,
  ExternalLink, Clock, Shield, Percent, Info
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const dealCategories = [
  { value: "cruise", label: "Croisières", icon: "🚢" },
  { value: "hotel", label: "Hôtels", icon: "🏨" },
  { value: "restaurant", label: "Restaurants", icon: "🍽️" },
  { value: "transport", label: "Transport", icon: "✈️" },
  { value: "activity", label: "Activités", icon: "🎯" },
  { value: "insurance", label: "Assurance", icon: "🛡️" },
  { value: "other", label: "Autre", icon: "🎁" },
];

const dealStatusOptions = [
  { value: "brouillon", label: "Brouillon", color: "bg-gray-100 text-gray-700", description: "Non visible" },
  { value: "en_negociation", label: "En négociation", color: "bg-yellow-100 text-yellow-700", description: "Non visible" },
  { value: "teaser_public", label: "Teaser public", color: "bg-blue-100 text-blue-700", description: "Aperçu public, détails adhérents" },
  { value: "reserve_adherents", label: "Réservé adhérents", color: "bg-purple-100 text-purple-700", description: "Visible adhérents uniquement" },
  { value: "actif", label: "Actif", color: "bg-green-100 text-green-700", description: "Teaser public + détails adhérents" },
  { value: "expire", label: "Expiré", color: "bg-red-100 text-red-700", description: "Non visible" },
];

const dealStatuses = [
  { value: "all", label: "Tous" },
  { value: "actif", label: "Actifs" },
  { value: "teaser_public", label: "Teaser public" },
  { value: "reserve_adherents", label: "Réservé adhérents" },
  { value: "en_negociation", label: "En négociation" },
  { value: "brouillon", label: "Brouillons" },
  { value: "expire", label: "Expirés" },
];

export default function DealsManagementPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedDeal, setExpandedDeal] = useState(null);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMembersOnly, setFilterMembersOnly] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [form, setForm] = useState({
    title: "",
    partner_id: "",
    partner_name: "",
    partner_logo: "",
    category: "cruise",
    description: "",
    description_full: "",
    // Teaser fields
    teaser_summary: "",
    teaser_benefits: [],
    teaser_image: "",
    teaser_cta_text: "Devenir adhérent",
    // Offer details
    discount: "",
    conditions: "",
    procedure: "",
    code: "",
    link_url: "",
    valid_from: "",
    valid_until: "",
    // Visibility settings
    is_members_only: true,
    is_published: false,
    show_on_home: false,
    enable_interest_form: false,
    status: "brouillon",
  });
  
  const [newBenefit, setNewBenefit] = useState("");

  useEffect(() => { loadDeals(); }, []);

  const loadDeals = async () => {
    try {
      // Use admin endpoint to get all deals including drafts
      const res = await getDealsAdmin({ include_drafts: true });
      setDeals(res.data);
    } catch (error) {
      console.error("Error loading deals:", error);
      // Fallback to regular endpoint if admin fails
      try {
        const res = await getDeals({ include_drafts: true });
        setDeals(res.data);
      } catch (e) {
        toast.error("Erreur lors du chargement des bons plans");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      partner_id: "",
      partner_name: "",
      partner_logo: "",
      category: "cruise",
      description: "",
      description_full: "",
      teaser_summary: "",
      teaser_benefits: [],
      teaser_image: "",
      teaser_cta_text: "Devenir adhérent",
      discount: "",
      conditions: "",
      procedure: "",
      code: "",
      link_url: "",
      valid_from: "",
      valid_until: "",
      is_members_only: true,
      is_published: false,
      show_on_home: false,
      enable_interest_form: false,
      status: "brouillon",
    });
    setEditingDeal(null);
    setNewBenefit("");
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setForm({
      title: deal.title || "",
      partner_id: deal.partner_id || "",
      partner_name: deal.partner_name || "",
      partner_logo: deal.partner_logo || "",
      category: deal.category || "cruise",
      description: deal.description || "",
      description_full: deal.description_full || "",
      teaser_summary: deal.teaser_summary || "",
      teaser_benefits: deal.teaser_benefits || [],
      teaser_image: deal.teaser_image || "",
      teaser_cta_text: deal.teaser_cta_text || "Devenir adhérent",
      discount: deal.discount || "",
      conditions: deal.conditions || "",
      procedure: deal.procedure || "",
      code: deal.code || "",
      link_url: deal.link_url || "",
      valid_from: deal.valid_from || "",
      valid_until: deal.valid_until || "",
      is_members_only: deal.is_members_only !== false,
      is_published: deal.is_published || false,
      show_on_home: deal.show_on_home || false,
      enable_interest_form: deal.enable_interest_form || false,
      status: deal.status || "brouillon",
    });
    setShowForm(true);
  };

  const handleDuplicate = (deal) => {
    setEditingDeal(null);
    setForm({
      ...deal,
      title: `${deal.title} (copie)`,
      is_published: false,
      status: "brouillon",
      teaser_benefits: deal.teaser_benefits || [],
    });
    setShowForm(true);
  };
  
  const addBenefit = () => {
    if (newBenefit.trim()) {
      setForm({
        ...form,
        teaser_benefits: [...(form.teaser_benefits || []), newBenefit.trim()]
      });
      setNewBenefit("");
    }
  };
  
  const removeBenefit = (index) => {
    setForm({
      ...form,
      teaser_benefits: form.teaser_benefits.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.partner_name.trim()) {
      toast.error("Le titre et le nom du partenaire sont requis");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        partner_id: form.partner_id || form.partner_name.toLowerCase().replace(/\s+/g, '-'),
      };

      if (editingDeal) {
        await updateDeal(editingDeal.id, data);
        toast.success("Bon plan mis à jour !");
      } else {
        await createDeal(data);
        toast.success("Bon plan créé !");
      }

      setShowForm(false);
      resetForm();
      loadDeals();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce bon plan ?")) return;
    try {
      await deleteDeal(id);
      toast.success("Bon plan supprimé");
      loadDeals();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const togglePublish = async (deal) => {
    try {
      await updateDeal(deal.id, { ...deal, is_published: !deal.is_published });
      toast.success(deal.is_published ? "Bon plan dépublié" : "Bon plan publié");
      loadDeals();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getDealStatus = (deal) => {
    // Use the explicit status field - this is the source of truth
    if (deal.status) {
      // Check for expiration for active/teaser statuses
      if (["actif", "teaser_public", "reserve_adherents"].includes(deal.status) && deal.valid_until) {
        const expiry = new Date(deal.valid_until);
        if (expiry < new Date()) return "expire";
      }
      return deal.status;
    }
    // Fallback for legacy deals without status field
    if (deal.valid_until) {
      const expiry = new Date(deal.valid_until);
      if (expiry < new Date()) return "expire";
    }
    return deal.is_published ? "actif" : "brouillon";
  };

  const getStatusBadge = (status) => {
    const statusOption = dealStatusOptions.find(s => s.value === status);
    const color = statusOption?.color || "bg-gray-100 text-gray-600";
    const label = statusOption?.label || status;
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${color}`}>
        {label}
      </span>
    );
  };

  const getCategoryIcon = (cat) => {
    const category = dealCategories.find(c => c.value === cat);
    return category?.icon || "🎁";
  };

  // Filter deals
  const filteredDeals = deals.filter(deal => {
    const status = getDealStatus(deal);
    if (filterCategory !== "all" && deal.category !== filterCategory) return false;
    if (filterStatus !== "all" && status !== filterStatus) return false;
    if (filterMembersOnly !== "all") {
      if (filterMembersOnly === "members" && !deal.is_members_only) return false;
      if (filterMembersOnly === "public" && deal.is_members_only) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        deal.title?.toLowerCase().includes(query) ||
        deal.partner_name?.toLowerCase().includes(query) ||
        deal.description?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Group by category
  const groupedDeals = filteredDeals.reduce((acc, deal) => {
    const cat = deal.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(deal);
    return acc;
  }, {});

  return (
    <div data-testid="deals-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
            <Gift className="h-8 w-8 text-accent" />
            Bons Plans & Avantages
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez les offres et avantages pour vos adhérents
          </p>
        </div>
        <Button 
          className="bg-accent hover:bg-accent/90 gap-2" 
          onClick={() => { setShowForm(true); resetForm(); }}
        >
          <Plus className="h-4 w-4" />
          Nouveau bon plan
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Rechercher un bon plan..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {dealCategories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              {dealStatuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMembersOnly} onValueChange={setFilterMembersOnly}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Visibilité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="members">Adhérents uniquement</SelectItem>
              <SelectItem value="public">Public</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Deal Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-auto shadow-2xl">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold">
                  {editingDeal ? "Modifier le bon plan" : "Nouveau bon plan"}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); resetForm(); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Titre de l'offre *</label>
                  <Input
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex: -15% sur votre prochaine croisière MSC"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Partenaire *</label>
                  <Input
                    value={form.partner_name}
                    onChange={e => setForm({ ...form, partner_name: e.target.value })}
                    placeholder="Ex: MSC Croisières"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Catégorie</label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dealCategories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Discount & Code */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Réduction/Avantage</label>
                  <Input
                    value={form.discount}
                    onChange={e => setForm({ ...form, discount: e.target.value })}
                    placeholder="Ex: -15%, 50€ offerts"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Code promo</label>
                  <Input
                    value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value })}
                    placeholder="Ex: ITERCATUS15"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Lien partenaire</label>
                  <Input
                    value={form.link_url}
                    onChange={e => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div>
                <label className="block text-sm font-medium mb-1">Description courte</label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Résumé de l'offre (affiché dans la liste)"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description complète</label>
                <Textarea
                  value={form.description_full}
                  onChange={e => setForm({ ...form, description_full: e.target.value })}
                  placeholder="Détails complets de l'offre (réservé aux adhérents si membres only)"
                  rows={4}
                />
              </div>

              {/* Teaser Section - Public Preview */}
              <div className="p-4 bg-blue-50 rounded-lg space-y-4 border border-blue-200">
                <div className="flex items-center gap-2 text-blue-700 font-medium">
                  <Globe className="h-4 w-4" />
                  Aperçu Public (Teaser)
                </div>
                <p className="text-xs text-blue-600">
                  Ces informations sont affichées publiquement pour inciter à l'adhésion. Les détails complets restent réservés aux adhérents.
                </p>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Résumé accrocheur</label>
                  <Textarea
                    value={form.teaser_summary}
                    onChange={e => setForm({ ...form, teaser_summary: e.target.value })}
                    placeholder="Ex: Profitez de réductions exclusives sur les plus belles croisières MSC en Méditerranée..."
                    rows={2}
                    className="bg-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Bénéfices généraux (liste)</label>
                  <div className="space-y-2">
                    {(form.teaser_benefits || []).map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="flex-1 bg-white px-3 py-1.5 rounded border text-sm">{benefit}</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeBenefit(index)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={newBenefit}
                        onChange={e => setNewBenefit(e.target.value)}
                        placeholder="Ex: Jusqu'à -15% sur les cabines balcon"
                        className="bg-white"
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={addBenefit}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Image du teaser (URL)</label>
                    <Input
                      value={form.teaser_image}
                      onChange={e => setForm({ ...form, teaser_image: e.target.value })}
                      placeholder="https://... (optionnel, sinon logo partenaire)"
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Texte du bouton CTA</label>
                    <Input
                      value={form.teaser_cta_text}
                      onChange={e => setForm({ ...form, teaser_cta_text: e.target.value })}
                      placeholder="Devenir adhérent"
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Conditions & Procedure */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Conditions d'utilisation</label>
                  <Textarea
                    value={form.conditions}
                    onChange={e => setForm({ ...form, conditions: e.target.value })}
                    placeholder="Ex: Non cumulable avec d'autres offres, valable sur les croisières de +7 jours..."
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Procédure pour en bénéficier</label>
                  <Textarea
                    value={form.procedure}
                    onChange={e => setForm({ ...form, procedure: e.target.value })}
                    placeholder="Ex: 1. Connectez-vous à votre espace adhérent. 2. Utilisez le code ITERCATUS15..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date de début</label>
                  <Input
                    type="date"
                    value={form.valid_from}
                    onChange={e => setForm({ ...form, valid_from: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date d'expiration</label>
                  <Input
                    type="date"
                    value={form.valid_until}
                    onChange={e => setForm({ ...form, valid_until: e.target.value })}
                  />
                </div>
              </div>

              {/* Options */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                {/* Status selector - Important for visibility */}
                <div>
                  <label className="block text-sm font-medium mb-2">Statut de l'avantage</label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger className="w-full md:w-[300px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dealStatusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${opt.color.split(' ')[0]}`}></span>
                            <span>{opt.label}</span>
                            <span className="text-xs text-muted-foreground">({opt.description})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-2 p-3 bg-white rounded border text-xs">
                    <p className="font-medium mb-1">Logique de visibilité :</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• <strong>Brouillon / En négociation</strong> : invisible partout</li>
                      <li>• <strong>Teaser public</strong> : aperçu public, détails adhérents</li>
                      <li>• <strong>Réservé adhérents</strong> : visible adhérents uniquement</li>
                      <li>• <strong>Actif</strong> : teaser public + détails adhérents</li>
                    </ul>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={form.is_members_only}
                      onCheckedChange={v => setForm({ ...form, is_members_only: v })}
                    />
                    <div>
                      <p className="font-medium text-sm">Réservé aux adhérents</p>
                      <p className="text-xs text-muted-foreground">Détails visibles uniquement pour les membres actifs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={form.is_published}
                      onCheckedChange={v => setForm({ ...form, is_published: v })}
                    />
                    <div>
                      <p className="font-medium text-sm">Publier</p>
                      <p className="text-xs text-muted-foreground">Autoriser l'affichage selon le statut</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={form.show_on_home}
                      onCheckedChange={v => setForm({ ...form, show_on_home: v })}
                    />
                    <div>
                      <p className="font-medium text-sm">Mettre en avant</p>
                      <p className="text-xs text-muted-foreground">Afficher sur la page d'accueil</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={form.enable_interest_form}
                      onCheckedChange={v => setForm({ ...form, enable_interest_form: v })}
                    />
                    <div>
                      <p className="font-medium text-sm">Formulaire d'intérêt</p>
                      <p className="text-xs text-muted-foreground">Permettre aux adhérents de manifester leur intérêt</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={saving}>
                  {saving ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enregistrement...</>
                  ) : (
                    <><Check className="h-4 w-4 mr-2" /> {editingDeal ? "Mettre à jour" : "Créer le bon plan"}</>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deals List */}
      {loading ? (
        <div className="space-y-4">
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : filteredDeals.length > 0 ? (
        <div className="space-y-8">
          {Object.entries(groupedDeals).map(([category, categoryDeals]) => {
            const catInfo = dealCategories.find(c => c.value === category) || { icon: "🎁", label: category };
            return (
              <div key={category}>
                <h2 className="font-heading text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <span>{catInfo.icon}</span>
                  {catInfo.label}
                  <span className="text-sm font-normal text-muted-foreground">({categoryDeals.length})</span>
                </h2>
                <div className="space-y-3">
                  {categoryDeals.map((deal) => {
                    const status = getDealStatus(deal);
                    const isExpanded = expandedDeal === deal.id;
                    
                    return (
                      <div 
                        key={deal.id} 
                        className={`bg-white border rounded-xl overflow-hidden transition-shadow hover:shadow-md ${
                          status === "expired" ? "opacity-60" : ""
                        }`}
                      >
                        {/* Deal Header */}
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <h3 className="font-heading text-lg font-semibold text-primary truncate">
                                  {deal.title}
                                </h3>
                                {deal.discount && (
                                  <span className="bg-accent/10 text-accent font-bold text-sm px-2 py-0.5 rounded">
                                    {deal.discount}
                                  </span>
                                )}
                                {getStatusBadge(status)}
                                {deal.is_members_only && (
                                  <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Shield className="h-3 w-3" /> Adhérents
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Building className="h-4 w-4" />
                                  {deal.partner_name}
                                </span>
                                {deal.valid_until && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    Jusqu'au {format(new Date(deal.valid_until), "d MMM yyyy", { locale: fr })}
                                  </span>
                                )}
                              </div>
                              {deal.description && (
                                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                  {deal.description}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setExpandedDeal(isExpanded ? null : deal.id)}
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(deal)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDuplicate(deal)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(deal.id)} className="text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="border-t bg-gray-50 p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {deal.conditions && (
                                <div>
                                  <h4 className="font-medium text-sm mb-1 flex items-center gap-1">
                                    <Info className="h-4 w-4 text-muted-foreground" />
                                    Conditions
                                  </h4>
                                  <p className="text-sm text-muted-foreground">{deal.conditions}</p>
                                </div>
                              )}
                              {deal.procedure && (
                                <div>
                                  <h4 className="font-medium text-sm mb-1">Procédure</h4>
                                  <p className="text-sm text-muted-foreground whitespace-pre-line">{deal.procedure}</p>
                                </div>
                              )}
                            </div>
                            {deal.code && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">Code promo :</span>
                                <code className="bg-white px-3 py-1 rounded border font-mono text-accent">
                                  {deal.code}
                                </code>
                              </div>
                            )}
                            {deal.link_url && (
                              <a 
                                href={deal.link_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Voir sur le site partenaire
                              </a>
                            )}
                            <div className="flex items-center gap-4 pt-2 border-t">
                              <Button
                                variant={deal.is_published ? "outline" : "default"}
                                size="sm"
                                onClick={() => togglePublish(deal)}
                                className={deal.is_published ? "" : "bg-green-600 hover:bg-green-700"}
                              >
                                {deal.is_published ? "Dépublier" : "Publier"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-white border rounded-xl">
          <Gift className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucun bon plan</h3>
          <p className="text-muted-foreground mb-6">
            {searchQuery || filterCategory !== "all" || filterStatus !== "all" 
              ? "Aucun résultat pour ces filtres"
              : "Commencez par créer votre premier bon plan partenaire"}
          </p>
          {!searchQuery && filterCategory === "all" && filterStatus === "all" && (
            <Button onClick={() => { setShowForm(true); resetForm(); }} className="bg-accent hover:bg-accent/90">
              <Plus className="h-4 w-4 mr-2" /> Créer un bon plan
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
